import { Phone, MessageSquare, CalendarClock, FileText, Snowflake } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSettings } from "@/contexts/SettingsContext";
import { onCallClick, onTextClick } from "@/lib/dialer";

export default function ContactCTA() {
  const { t } = useLang();
  const { settings } = useSettings();
  const actions = [
    { href: `tel:${COMPANY.phoneTel}`, label: t("cta.call"), Icon: Phone, primary: true, testid: "cta-call", onClick: onCallClick },
    { href: COMPANY.sms, label: t("cta.text"), Icon: MessageSquare, primary: false, testid: "cta-text", onClick: onTextClick },
    { to: "/schedule", label: t("cta.schedule"), Icon: CalendarClock, primary: false, testid: "cta-schedule" },
    { hash: "#estimate", label: t("cta.estimate"), Icon: FileText, primary: false, testid: "cta-estimate" },
  ];

  return (
    <section id="contact-cta" className="py-14 md:py-20" data-testid="contact-cta">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <div className="rounded-3xl bg-gradient-to-br from-ice-400/20 via-ink-800 to-ink-800 ring-1 ring-ice-400/30 p-7 md:p-10 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 size-72 rounded-full bg-ice-400/15 blur-3xl"/>

          <div className="flex items-center gap-4 mb-5" data-testid="contact-cta-logo">
            {settings.logo ? (
              <img src={settings.logo} alt="AIR-COLD HVAC logo" className="h-16 md:h-20 w-auto rounded-md object-contain"/>
            ) : (
              <>
                <div className="size-12 rounded-2xl bg-ice-400/15 ring-1 ring-ice-400/40 grid place-items-center">
                  <Snowflake className="size-6 text-ice-400"/>
                </div>
                <div className="leading-tight">
                  <div className="font-display font-black text-xl">AIR-COLD</div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-silver-300">HVAC</div>
                </div>
              </>
            )}
          </div>

          <div className="text-xs uppercase tracking-[0.22em] text-ice-400">Contact AIR-COLD HVAC</div>
          <h2 className="mt-3 font-display text-3xl md:text-5xl font-black">Ready to get started?</h2>
          <a href={`tel:${COMPANY.phoneTel}`} onClick={onCallClick} className="mt-3 inline-flex items-baseline gap-2 font-display font-black text-2xl md:text-4xl text-ice-400 hover:underline" data-testid="contact-cta-phone">
            <Phone className="size-5 self-center"/> {COMPANY.phone}
          </a>
          <p className="mt-3 text-silver-300 max-w-md text-sm md:text-base">Licensed, insured, and ready. Call, text, schedule, or request a free estimate — we respond fast.</p>

          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-3">
            {actions.map((a, i) => {
              const Icon = a.Icon;
              const cls = `${a.primary ? "btn-primary" : "btn-ghost"} rounded-full px-4 py-3.5 text-sm font-semibold flex items-center justify-center gap-2`;
              if (a.to) return <Link key={i} to={a.to} className={cls} data-testid={a.testid}><Icon className="size-4"/> {a.label}</Link>;
              if (a.hash) return <a key={i} href={a.hash} className={cls} data-testid={a.testid}><Icon className="size-4"/> {a.label}</a>;
              if (a.href) return <a key={a.testid} href={a.href} onClick={a.onClick} className={cls} data-testid={a.testid}><Icon className="size-4"/> {a.label}</a>;
              return null;
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
